import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo } from 'react';
import { message } from 'antd';
import { CHANNEL_MEDIA_PLAN_STATUS } from '@skyrise-eng/types';
import { MenuItems, UploadPDF, UseActions } from '../Actions.types';
import { useDeleteMediaPlanMutation, useLazyGetCampaignSchedulePDFQuery } from '@/api';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { useRole } from '@/hooks/useRole';
import { APP_SCOPE, NAMESPACE, DELETE_ACTION_COLOR } from '@/constants';
import { buildPDF } from '@/utils';

export const useActions: UseActions = ({ id, status, campaignName }) => {
  const { canDelete, canUpdate, canCreate } = useRole(APP_SCOPE.MEDIA_PLAN);
  const { t } = useTranslation(NAMESPACE.MEDIA_PLAN_PAGE);
  const [deleteMediaPlan, { isLoading: isDeleting, isSuccess: isDeletionSuccessful }] =
    useDeleteMediaPlanMutation();
  const [getCampaignSchedulePDF] = useLazyGetCampaignSchedulePDFQuery();
  const { handleConfirm: handleDeleteMediaPlan } = useConfirmModal({
    title: t('deleteMediaPlanModalTitle'),
    content: t('deleteMediaPlanModalDescription'),
    destructive: true,
    okText: t('cancel', { ns: NAMESPACE.COMMON }),
    cancelText: t('delete', { ns: NAMESPACE.COMMON }),
    onCancel: () => deleteMediaPlan(id)
  });

  useEffect(() => {
    if (isDeletionSuccessful) {
      message.success(t('mediaPlanHasBeenDeleted'));
    }
  }, [isDeletionSuccessful, t]);

  const uploadPDF: UploadPDF = useCallback(
    async ({ domEvent }) => {
      domEvent.stopPropagation();
      try {
        const result = await getCampaignSchedulePDF(id);
        const { data, error } = result;

        if (error) {
          const errMsg =
            (error as { data?: { message?: string } })?.data?.message ??
            (error as { message?: string })?.message ??
            t('pdfDownloadFailed');
          console.error('[PDF] API error:', error);
          message.error(errMsg);
          return;
        }

        if (data) {
          await buildPDF(campaignName, data);
        } else {
          console.error('[PDF] No data returned from API');
          message.error(t('pdfDownloadFailed'));
        }
      } catch (err) {
        console.error('[PDF] Error:', err);
        message.error(t('pdfDownloadFailed'));
      }
    },
    [getCampaignSchedulePDF, id, campaignName, t]
  );

  const menuItems: MenuItems = useMemo(() => {
    const items: MenuItems = [];

    if (canCreate) {
      items.push({
        key: 1,
        label: t('saveAsPDF'),
        disabled: status !== CHANNEL_MEDIA_PLAN_STATUS.READY,
        onClick: uploadPDF
      });
    }

    if (canDelete) {
      items.push({
        key: 2,
        disabled: isDeleting,
        label: <span style={{ color: DELETE_ACTION_COLOR }}>{t('delete', { ns: 'common' })}</span>,
        onClick: ({ domEvent }) => {
          domEvent.stopPropagation();
          handleDeleteMediaPlan();
        },
        danger: true
      });
    }

    return items;
  }, [canCreate, canDelete, t, status, uploadPDF, isDeleting, handleDeleteMediaPlan]);

  return {
    menuItems,
    canUpdate
  };
};
